import { useState } from 'react';
import { useSimpleApp } from '../context/SimpleAppContext';

export function GapDashboard() {
 const { gapData, updateGapItem, addGapItem, authState } = useSimpleApp();
 const [selectedSeverity, setSelectedSeverity] = useState<string | null>(null);
 const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
 const [editingGap, setEditingGap] = useState<string | null>(null);
 const [editingField, setEditingField] = useState<string | null>(null);
 const [editValue, setEditValue] = useState<string>('');
 const [isAddingNew, setIsAddingNew] = useState(false);
 const [newGap, setNewGap] = useState({
  title: '',
  description: '',
  severity: 'Medium' as 'Critical' | 'High' | 'Medium' | 'Low',
  categories: [] as ('Regulatory' | 'Commercial' | 'Operational' | 'IP')[],
  timeline: '',
  status: 'Not Started' as 'Not Started' | 'In Progress' | 'Completed' | 'Blocked',
  recommendation: ''
 });

 const isAdmin = authState.user?.role === 'admin';

 const availableCategories = ['Regulatory', 'Commercial', 'Operational', 'IP'] as const;

 const handleCategoryChange = (gapId: string, category: string, isChecked: boolean) => {
  const gap = gapData.gaps.find(g => g.id === gapId);
  if (!gap) return;

  const currentCategories = gap.categories || [];
  let newCategories: ('Regulatory' | 'Commercial' | 'Operational' | 'IP')[];
  if (isChecked) {
   newCategories = [...currentCategories, category as any];
  } else {
   newCategories = currentCategories.filter(cat => cat !== category);
  }

  updateGapItem(gapId, { categories: newCategories });
 };

 const handleNewGapCategoryChange = (category: string, isChecked: boolean) => {
  let newCategories: typeof newGap.categories;
  if (isChecked) {
   newCategories = [...newGap.categories, category as any];
  } else {
   newCategories = newGap.categories.filter(cat => cat !== category);
  }
  setNewGap({ ...newGap, categories: newCategories });
 };

 const severityColors = {
  'Critical': 'bg-red-500 border-red-600 text-white',
  'High': 'bg-orange-500 border-orange-600 text-white',
  'Medium': 'bg-yellow-500 border-yellow-600 text-white',
  'Low': 'bg-blue-500 border-blue-600 text-white'
 };

 const statusColors = {
  'Not Started': 'bg-gray-200 text-gray-800',
  'In Progress': 'bg-yellow-200 text-yellow-800',
  'Completed': 'bg-green-200 text-green-800',
  'Blocked': 'bg-red-200 text-red-800'
 };

 const filteredGaps = gapData.gaps.filter(gap => {
  if (selectedSeverity && gap.severity !== selectedSeverity) return false;
  if (selectedCategory && !(gap.categories || []).includes(selectedCategory as any)) return false;
  return true;
 });

 const handleEdit = (gapId: string, field: string, currentValue: string) => {
  setEditingGap(gapId);
  setEditingField(field);
  setEditValue(currentValue);
 };

 const handleSave = () => {
  if (editingGap && editingField) {
   updateGapItem(editingGap, { [editingField]: editValue });
   setEditingGap(null);
   setEditingField(null);
   setEditValue('');
  }
 };

 const handleCancel = () => {
  setEditingGap(null);
  setEditingField(null);
  setEditValue('');
 };

 const handleAddNew = () => {
  setIsAddingNew(true);
 };

 const handleSaveNew = () => {
  if (newGap.title.trim() && newGap.description.trim()) {
   addGapItem({
    title: newGap.title.trim(),
    description: newGap.description.trim(),
    severity: newGap.severity,
    categories: newGap.categories.length > 0 ? newGap.categories : ['Commercial'],
    timeline: newGap.timeline.trim() || 'TBD',
    status: newGap.status,
    recommendation: newGap.recommendation.trim()
   });
   
   // Reset form
   setNewGap({
    title: '',
    description: '',
    severity: 'Medium',
    categories: [],
    timeline: '',
    status: 'Not Started',
    recommendation: ''
   });
   setIsAddingNew(false);
  }
 };

 const handleCancelNew = () => {
  setNewGap({
   title: '',
   description: '',
   severity: 'Medium',
   categories: [],
   timeline: '',
   status: 'Not Started',
   recommendation: ''
  });
  setIsAddingNew(false);
 };

 const exportGapData = () => {
  // Clean the data to remove old "category" and "evidence" properties and ensure only new format
  const cleanedGaps = gapData.gaps.map(gap => {
   const { category, evidence, ...cleanGap } = gap as any; // Remove old properties
   return {
    ...cleanGap,
    // Ensure categories exists and fallback to old category if needed
    categories: gap.categories || (category ? [category] : ['Commercial'])
   };
  });

  const gapDataContent = `import { GapData, GapItem } from '../types/scorecard';

export const initialGapData: GapItem[] = ${JSON.stringify(cleanedGaps, null, 2).replace(/"(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)"/g, 'new Date("$1")')};

export const defaultGapData: GapData = {
 gaps: initialGapData,
 lastModified: new Date()
};`;

  const blob = new Blob([gapDataContent], { type: 'text/typescript' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'gapData.ts';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  alert(` Gap Analysis data exported successfully!

 AUTOMATIC CLEANUP: Old "category" properties have been removed from the export.
The downloaded file only contains the new "categories" array format.

To update with new Word documents:
1. Save your changes: Replace src/data/gapData.ts with the downloaded file
2. Upload new Word files: Place updated .docx files in the project root folder 
3. Process updates: Run 'python3 update_gap_data.py [filename.docx]'
4. Refresh dashboard to see changes

 The exported file is TypeScript-compliant and ready to replace the data file!
File downloaded: gapData.ts`);
 };




 return (
  <div className="flex flex-col h-full overflow-hidden">
   {/* Filters and Summary */}
   <div className="flex-none p-4 bg-gray-50 border-b border-gray-200">
    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
     {/* Summary Stats */}
     <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-white p-3 rounded-lg shadow-sm">
       <div className="text-2xl font-bold text-red-600">
        {gapData.gaps.filter(g => g.severity === 'Critical').length}
       </div>
       <div className="text-sm text-gray-600">Critical</div>
      </div>
      <div className="bg-white p-3 rounded-lg shadow-sm">
       <div className="text-2xl font-bold text-orange-600">
        {gapData.gaps.filter(g => g.severity === 'High').length}
       </div>
       <div className="text-sm text-gray-600">High</div>
      </div>
      <div className="bg-white p-3 rounded-lg shadow-sm">
       <div className="text-2xl font-bold text-yellow-600">
        {gapData.gaps.filter(g => g.status === 'In Progress').length}
       </div>
       <div className="text-sm text-gray-600">In Progress</div>
      </div>
      <div className="bg-white p-3 rounded-lg shadow-sm">
       <div className="text-2xl font-bold text-green-600">
        {gapData.gaps.filter(g => g.status === 'Completed').length}
       </div>
       <div className="text-sm text-gray-600">Completed</div>
      </div>
     </div>

     {/* Filters */}
     <div className="flex flex-wrap gap-2">
      <select
       value={selectedSeverity || ''}
       onChange={(e) => setSelectedSeverity(e.target.value || null)}
       className="text-sm border border-gray-300 rounded px-3 py-1"
      >
       <option value="">All Severities</option>
       <option value="Critical">Critical</option>
       <option value="High">High</option>
       <option value="Medium">Medium</option>
       <option value="Low">Low</option>
      </select>
      <select
       value={selectedCategory || ''}
       onChange={(e) => setSelectedCategory(e.target.value || null)}
       className="text-sm border border-gray-300 rounded px-3 py-1"
      >
       <option value="">All Categories</option>
       <option value="Regulatory">Regulatory</option>
       <option value="Commercial">Commercial</option>
       <option value="Operational">Operational</option>
       <option value="IP">IP</option>
      </select>
      {isAdmin && (
       <div className="flex gap-2">
        <button
         onClick={handleAddNew}
         className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-1 text-sm font-semibold rounded shadow-md hover:shadow-lg transition-all duration-200"
        >
          Add Gap
        </button>
        <button
         onClick={exportGapData}
         className="bg-gradient-to-r from-cardio-500 to-cardio-600 hover:from-cardio-600 hover:to-cardio-700 text-white px-4 py-1 text-sm font-semibold rounded shadow-md hover:shadow-lg transition-all duration-200"
        >
          Save
        </button>
       </div>
      )}
     </div>
    </div>
   </div>

   {/* Gap Items */}
   <div className="flex-1 overflow-y-auto p-4">
    <div className="grid gap-4">
     {/* Add New Gap Form */}
     {isAddingNew && isAdmin && (
      <div className="bg-blue-50 rounded-lg shadow-md border-2 border-blue-200 p-4 lg:p-6">
       <h3 className="text-lg font-semibold text-blue-900 mb-4">Add New Gap</h3>
       
       <div className="space-y-4">
        {/* Title and Basic Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
         <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
           type="text"
           value={newGap.title}
           onChange={(e) => setNewGap({ ...newGap, title: e.target.value })}
           className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
           placeholder="Gap title"
           required
          />
         </div>
         <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
          <input
           type="text"
           value={newGap.timeline}
           onChange={(e) => setNewGap({ ...newGap, timeline: e.target.value })}
           className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
           placeholder="e.g., 3-6 months"
          />
         </div>
        </div>

        {/* Severity and Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
         <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
          <select
           value={newGap.severity}
           onChange={(e) => setNewGap({ ...newGap, severity: e.target.value as any })}
           className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
           <option value="Critical">Critical</option>
           <option value="High">High</option>
           <option value="Medium">Medium</option>
           <option value="Low">Low</option>
          </select>
         </div>
         <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
           value={newGap.status}
           onChange={(e) => setNewGap({ ...newGap, status: e.target.value as any })}
           className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
           <option value="Not Started">Not Started</option>
           <option value="In Progress">In Progress</option>
           <option value="Completed">Completed</option>
           <option value="Blocked">Blocked</option>
          </select>
         </div>
        </div>

        {/* Categories */}
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1">Categories (select multiple)</label>
         <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 p-3 border border-gray-300 rounded-md bg-gray-50">
          {availableCategories.map(category => (
           <label key={category} className="flex items-center gap-2 cursor-pointer">
            <input
             type="checkbox"
             checked={newGap.categories.includes(category)}
             onChange={(e) => handleNewGapCategoryChange(category, e.target.checked)}
             className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm">{category}</span>
           </label>
          ))}
         </div>
        </div>

        {/* Description */}
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
         <textarea
          value={newGap.description}
          onChange={(e) => setNewGap({ ...newGap, description: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          placeholder="Describe the gap"
          required
         />
        </div>

        {/* Recommendation */}
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1">Recommendation</label>
         <textarea
          value={newGap.recommendation}
          onChange={(e) => setNewGap({ ...newGap, recommendation: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={2}
          placeholder="Recommended action"
         />
        </div>


        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
         <button
          onClick={handleSaveNew}
          disabled={!newGap.title.trim() || !newGap.description.trim()}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
         >
           Save Gap
         </button>
         <button
          onClick={handleCancelNew}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium"
         >
          Cancel
         </button>
        </div>
       </div>
      </div>
     )}

     {filteredGaps.map((gap) => (
      <div
       key={gap.id}
       className="bg-white rounded-lg shadow-md border border-gray-200 p-4 lg:p-6"
      >
       {/* Gap Header */}
       <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-3 mb-4">
        <div className="flex-1">
         <div className="flex flex-wrap items-center gap-2 mb-2">
          {/* Severity Selector */}
          {isAdmin ? (
           <select
            value={gap.severity}
            onChange={(e) => updateGapItem(gap.id, { severity: e.target.value as any })}
            className={`px-2 py-1 rounded text-xs font-semibold border-0 ${severityColors[gap.severity]} cursor-pointer`}
           >
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
           </select>
          ) : (
           <span className={`px-2 py-1 rounded text-xs font-semibold ${severityColors[gap.severity]}`}>
            {gap.severity}
           </span>
          )}
          
          {/* Categories - Multiple selection for admins */}
          {isAdmin ? (
           <div className="flex flex-wrap gap-1">
            {availableCategories.map(category => (
             <label key={category} className="flex items-center gap-1 cursor-pointer">
              <input
               type="checkbox"
               checked={(gap.categories || []).includes(category)}
               onChange={(e) => handleCategoryChange(gap.id, category, e.target.checked)}
               className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
              />
              <span className={`px-2 py-1 rounded text-xs ${(gap.categories || []).includes(category) ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
               {category}
              </span>
             </label>
            ))}
           </div>
          ) : (
           <div className="flex flex-wrap gap-1">
            {(gap.categories || []).map(category => (
             <span key={category} className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
              {category}
             </span>
            ))}
           </div>
          )}
          
          {/* Status Selector */}
          {isAdmin ? (
           <select
            value={gap.status}
            onChange={(e) => updateGapItem(gap.id, { status: e.target.value as any })}
            className={`px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer ${statusColors[gap.status]}`}
           >
            <option value="Not Started">Not Started</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="Blocked">Blocked</option>
           </select>
          ) : (
           <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[gap.status]}`}>
            {gap.status}
           </span>
          )}
          
          {/* Completion Checkbox */}
          {isAdmin && (
           <label className="flex items-center gap-1 cursor-pointer">
            <input
             type="checkbox"
             checked={gap.status === 'Completed'}
             onChange={(e) => updateGapItem(gap.id, { status: e.target.checked ? 'Completed' : 'Not Started' })}
             className="rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-xs text-gray-600">Done</span>
           </label>
          )}
         </div>
         {editingGap === gap.id && editingField === 'title' ? (
          <div className="mb-2">
           <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full text-lg font-semibold p-2 border rounded"
            autoFocus
            onKeyDown={(e) => {
             if (e.key === 'Enter') handleSave();
             if (e.key === 'Escape') handleCancel();
            }}
           />
           <div className="flex gap-2 mt-1">
            <button onClick={handleSave} className="text-sm bg-blue-500 text-white px-3 py-1 rounded">
             Save
            </button>
            <button onClick={handleCancel} className="text-sm bg-gray-500 text-white px-3 py-1 rounded">
             Cancel
            </button>
           </div>
          </div>
         ) : (
          <h3 
           className={`text-lg font-semibold text-gray-900 mb-2 ${isAdmin ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
           onClick={isAdmin ? () => handleEdit(gap.id, 'title', gap.title) : undefined}
          >
           {gap.title} {isAdmin && <span className="text-xs text-gray-400">(click to edit)</span>}
          </h3>
         )}
        </div>
        <div className="text-sm text-gray-500">
         Timeline: {gap.timeline}
        </div>
       </div>

       {/* Gap Content */}
       <div className="space-y-4">
        <div>
         <h4 className="text-sm font-semibold text-gray-700 mb-1">Description</h4>
         {editingGap === gap.id && editingField === 'description' ? (
          <div>
           <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full p-2 border rounded text-sm"
            rows={3}
            autoFocus
            onKeyDown={(e) => {
             if (e.key === 'Enter' && e.ctrlKey) handleSave();
             if (e.key === 'Escape') handleCancel();
            }}
           />
           <div className="flex gap-2 mt-1">
            <button onClick={handleSave} className="text-sm bg-blue-500 text-white px-3 py-1 rounded">
             Save
            </button>
            <button onClick={handleCancel} className="text-sm bg-gray-500 text-white px-3 py-1 rounded">
             Cancel
            </button>
           </div>
          </div>
         ) : (
          <p 
           className={`text-sm text-gray-600 ${isAdmin ? 'cursor-pointer hover:bg-gray-50 p-1 rounded' : ''}`}
           onClick={isAdmin ? () => handleEdit(gap.id, 'description', gap.description) : undefined}
          >
           {gap.description} {isAdmin && <span className="text-xs text-gray-400">(click to edit)</span>}
          </p>
         )}
        </div>

        <div>
         <h4 className="text-sm font-semibold text-gray-700 mb-1">Recommendation</h4>
         {editingGap === gap.id && editingField === 'recommendation' ? (
          <div>
           <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full p-2 border rounded text-sm"
            rows={2}
            autoFocus
            onKeyDown={(e) => {
             if (e.key === 'Enter' && e.ctrlKey) handleSave();
             if (e.key === 'Escape') handleCancel();
            }}
           />
           <div className="flex gap-2 mt-1">
            <button onClick={handleSave} className="text-sm bg-blue-500 text-white px-3 py-1 rounded">
             Save
            </button>
            <button onClick={handleCancel} className="text-sm bg-gray-500 text-white px-3 py-1 rounded">
             Cancel
            </button>
           </div>
          </div>
         ) : (
          <p 
           className={`text-sm text-green-700 bg-green-50 p-2 rounded ${isAdmin ? 'cursor-pointer hover:bg-green-100' : ''}`}
           onClick={isAdmin ? () => handleEdit(gap.id, 'recommendation', gap.recommendation) : undefined}
          >
           {gap.recommendation} {isAdmin && <span className="text-xs text-green-400">(click to edit)</span>}
          </p>
         )}
        </div>


       </div>
      </div>
     ))}
    </div>
   </div>
  </div>
 );
}